// Clone-lander dialog (FF-24 / MIS-1007).
//
// Opened from a lander row (landers index; the domain analytics page — FF-22 —
// imports it too). Posts to the FF-9 endpoint
// `POST /api/landers/:id/clone` with `{ targetDomainId?, count }`: the target
// defaults to the lander's OWN domain (body key omitted), `count` is bounded
// 1–20 server-side. On 201 the response is `{ landers }` — the created rows
// each carry a fresh FF-2 random slug, which this dialog then lists (the
// user's first sight of them). Invalidates ['landers'], ['domains'] and the
// ['setup-status'] prefix (both the per-domain and aggregate keys share it).
//
// RPC gotcha (same as the lander edit PATCH): the clone route parses its body
// with `c.req.json()` (no zValidator), so the `{ param, json }` form fails
// typecheck — send the body through the typed `init` escape hatch instead;
// useApi()'s fetch preserves the body + Content-Type and adds auth headers.
//
// FF-9 pool gotcha: a cross-domain clone of a lander whose `numberPool.mode`
// is 'pool' lands with `{ mode: 'disabled' }` (pools/DIDs are domain-scoped),
// so when the SOURCE uses a pool we show an inline notice up front.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Lander } from '@/components/landers/types';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';

interface CloneLanderDialogProps {
  /** The source lander to clone. */
  landerId: number;
  /** The source lander's domain — pre-selected as the default clone target. */
  sourceDomainId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CloneLanderDialog({
  landerId,
  sourceDomainId,
  open,
  onOpenChange,
}: CloneLanderDialogProps): React.ReactElement {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeTenantId } = useTenantContext();

  // '' = clone into the source's own domain (omit `targetDomainId` — the
  // server defaults it). Any other value is a cross-domain clone.
  const [targetDomainId, setTargetDomainId] = useState<string>('');
  const [count, setCount] = useState<string>('1');
  // Set on 201 — flips the dialog into its "here are your new slugs" state.
  const [createdSlugs, setCreatedSlugs] = useState<string[] | null>(null);

  // Same key as the list pages so the cache slot is shared.
  const { data: domains } = useQuery({
    queryKey: ['domains', activeTenantId],
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) throw new Error('Failed to load domains');
      const data = await res.json();
      return data.domains;
    },
    enabled: open,
  });

  // The source lander — read for its `numberPool.mode` so the pool-reset
  // notice can render BEFORE the user clones. Same detail key shape as the
  // edit page; the 403 RBAC branch collapses inference, so assert `{ lander }`.
  const { data: sourceData } = useQuery({
    queryKey: ['landers', 'detail', String(landerId)],
    queryFn: async (): Promise<{ lander: Lander }> => {
      const res = await api.api.landers[':id'].$get({ param: { id: String(landerId) } });
      if (!res.ok) throw new Error(`Failed to load lander (${res.status})`);
      return (await res.json()) as { lander: Lander };
    },
    enabled: open,
  });
  const sourcePoolMode = sourceData?.lander.numberPool?.mode;

  const cloneMutation = useMutation({
    mutationFn: async () => {
      const parsedCount = Number(count);
      const body: { targetDomainId?: number; count: number } = {
        count: Number.isInteger(parsedCount) ? parsedCount : 1,
        ...(targetDomainId !== '' ? { targetDomainId: Number(targetDomainId) } : {}),
      };
      // No zValidator on the route → `{ param, json }` fails typecheck; the
      // typed `init` escape hatch carries the JSON body instead.
      const res = await api.api.landers[':id'].clone.$post(
        { param: { id: String(landerId) } },
        { init: { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } } },
      );
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('You need edit access on both the source and target domains.');
        }
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        if (data?.error === 'slug_exhausted') {
          throw new Error('Could not find a free random slug — try again.');
        }
        throw new Error(data?.error ?? `Clone failed (${res.status})`);
      }
      // 201 `{ landers }` — created rows with their server-generated slugs.
      return (await res.json()) as { landers: Lander[] };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['landers'] });
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      // Prefix match: covers ['setup-status', 'all', …] AND ['setup-status', id].
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
      setCreatedSlugs(data.landers.map((l) => l.slug));
      toast({
        title:
          data.landers.length === 1 ? 'Lander cloned' : `${data.landers.length} landers cloned`,
        variant: 'success',
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Could not clone lander', description: err.message, variant: 'destructive' });
    },
  });

  // Reset everything when the dialog closes so stale state never leaks into
  // the next open (mirrors NewLanderDialog).
  useEffect(() => {
    if (!open) {
      cloneMutation.reset();
      setTargetDomainId('');
      setCount('1');
      setCreatedSlugs(null);
    }
  }, [open, cloneMutation.reset]);

  const parsedCount = Number(count);
  const countValid = Number.isInteger(parsedCount) && parsedCount >= 1 && parsedCount <= 20;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} ariaLabel="Clone lander">
      <DialogHeader>
        <DialogTitle>Clone lander</DialogTitle>
        <DialogDescription>
          Copies the full config; each copy gets a fresh random slug.
        </DialogDescription>
      </DialogHeader>
      {createdSlugs !== null ? (
        <>
          <DialogBody className="space-y-3">
            <p className="text-sm text-gray-700">
              Created {createdSlugs.length} {createdSlugs.length === 1 ? 'lander' : 'landers'}:
            </p>
            <ul className="space-y-1">
              {createdSlugs.map((slug) => (
                <li key={slug} className="font-mono text-sm text-gray-900">
                  {slug}
                </li>
              ))}
            </ul>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogBody className="space-y-5">
            {/* FF-9 gotcha surfaced up front: pools are domain-scoped, so a
                cross-domain clone lands with number routing disabled. */}
            {sourcePoolMode === 'pool' && (
              <p
                role="alert"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700"
              >
                This lander routes calls through a number pool. Cloning to a different domain resets
                number routing to disabled — pools are domain-scoped.
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="clone-lander-domain">Target domain</Label>
              <Select
                id="clone-lander-domain"
                value={targetDomainId}
                onChange={(e) => setTargetDomainId(e.target.value)}
                disabled={cloneMutation.isPending}
              >
                <option value="">Same domain</option>
                {(domains ?? [])
                  .filter((d) => d.id !== sourceDomainId)
                  .map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.host}
                    </option>
                  ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="clone-lander-count">Copies (1–20)</Label>
              <Input
                id="clone-lander-count"
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                disabled={cloneMutation.isPending}
              />
              {!countValid && (
                <p className="text-sm text-red-600">Enter a number between 1 and 20.</p>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={cloneMutation.isPending || !countValid}
              onClick={() => cloneMutation.mutate()}
            >
              {cloneMutation.isPending ? 'Cloning…' : 'Clone'}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
